import axios, { AxiosError } from 'axios'
import { GeoJSON } from 'geojson'
import { DateTime } from 'luxon'

import { makeURL } from '@/api/config'
import { ServerError } from '@/api/errors/ServerError'

import { CityCollectionDTO } from './dto/city/CityCollectionDTO'
import { CityCollectionDTOFactory } from './dto/city/CityCollectionDTOFactory'
import { CityCollectionRaw } from './dto/city/CityCollectionRaw'
import { SnapshotValueDTO } from './dto/snapshot/SnapshotValueDTO'
import { SnapshotValueDTOFactory } from './dto/snapshot/SnapshotValueDTOFactory'
import { SnapshotValueRaw } from './dto/snapshot/SnapshotValueRaw'
import { AdministrativeAreaDTO } from './dto/snapshot/AdministrativeAreaDTO'
import { AdministrativeAreaDTOFactory } from './dto/snapshot/AdministrativeAreaDTOFactory'
import { AdministrativeAreaRaw } from './dto/snapshot/AdministrativeAreaRaw'
import { CatchmentRaw } from './dto/snapshot/CatchmentRaw'
import { CatchmentDTOFactory } from './dto/snapshot/CatchmentDTOFactory'
import { CatchmentDTO } from './dto/snapshot/CatchmentDTO'
import { SnapshotCumulativeRainfallDTO } from './dto/snapshot/SnapshotCumulativeRainfallDTO'
import { SnapshotCumulativeRainfallRaw } from './dto/snapshot/SnapshotCumulativeRainfallRaw'
import { SnapshotCumulativeRainfallDTOFactory } from './dto/snapshot/SnapshotCumulativeRainfallDTOFactory'

export class CoreAPI {
  private routes = {
    getCities: () => makeURL('/api/cities'),

    getSnapshot: (cityName: string, ref: DateTime) => makeURL(`/api/cities/${cityName}/timestep/${ref.toISO()}`),
    getCumulativeRainfallSnapshot: (cityName: string, ref: DateTime) =>
      makeURL(`/api/cities/${cityName}/cumulative_rainfall/timestep/${ref.toISO()}`),

    getAdministrativeAreas: (cityName: string, ref: DateTime) =>
      makeURL(`/api/cities/${cityName}/administrative_areas/${ref.toISO()}`),

    getAdministrativeAreasForecast: (cityName: string, ref: DateTime, forecast: DateTime) =>
      makeURL(`/api/cities/${cityName}/administrative_areas/${ref.toISO()}/${forecast.toISO()}`),

    getCatchments: (cityName: string, ref: DateTime) => makeURL(`/api/cities/${cityName}/catchments/${ref.toISO()}`),

    getCatchmentsForecast: (cityName: string, ref: DateTime, forecast: DateTime) =>
      makeURL(`/api/cities/${cityName}/catchments/${ref.toISO()}/${forecast.toISO()}`),

    getRainfallMap: (cityName: string, ref: DateTime) => makeURL(`/api/maps/${cityName}/rainfall/${ref.toISO()}`),

    getRainfallForecastMap: (cityName: string, ref: DateTime, forecast: DateTime) =>
      makeURL(`/api/maps/${cityName}/rainfall/${ref.toISO()}/${forecast.toISO()}`),

    getRainfallMaxMap: (cityName: string, ref: DateTime) =>
      makeURL(`/api/maps/${cityName}/max_rainfall/${ref.toISO()}`),

    getCumulativeRainfallMap: (cityName: string, cumulative: number, ref: DateTime) =>
      makeURL(`/api/maps/${cityName}/cumulative-rainfall/${cumulative}/${ref.toISO()}`),

    getCumulativeRainfallForecastMap: (cityName: string, cumulative: number, ref: DateTime, forecast: DateTime) =>
      makeURL(`/api/maps/${cityName}/cumulative-rainfall/${cumulative}/${ref.toISO()}/${forecast.toISO()}`),

    getReturnPeriodMap: (cityName: string, ref: DateTime) => makeURL(`/api/maps/${cityName}/rt/${ref.toISO()}`),

    getReturnPeriodForecastMap: (cityName: string, ref: DateTime, forecast: DateTime) =>
      makeURL(`/api/maps/${cityName}/rt/${ref.toISO()}/${forecast.toISO()}`),

    getReturnPeriodMaxMap: (cityName: string, ref: DateTime) => makeURL(`/api/maps/${cityName}/max_rt/${ref.toISO()}`),
  } as const

  private async safeGet<T>(url: string): Promise<T | null> {
    try {
      const result = await axios.get<T>(url)
      return result.data
    } catch (exception) {
      this.handleError(url, exception as AxiosError<ServerError>)
      return null
    }
  }

  public async getCities(): Promise<CityCollectionDTO | null> {
    const url = this.routes.getCities()
    const data = await this.safeGet<CityCollectionRaw>(url)
    return data ? CityCollectionDTOFactory.fromRaw(data) : null
  }

  public async getValueSnapshot(cityName: string, ref: DateTime): Promise<SnapshotValueDTO | null> {
    const url = this.routes.getSnapshot(cityName, ref)
    const data = await this.safeGet<SnapshotValueRaw>(url)
    return data ? SnapshotValueDTOFactory.fromRaw(data) : null
  }
  public async getCumulativeRainfallSnapshot(
    cityName: string,
    ref: DateTime,
  ): Promise<SnapshotCumulativeRainfallDTO | null> {
    const url = this.routes.getCumulativeRainfallSnapshot(cityName, ref)
    const data = await this.safeGet<SnapshotCumulativeRainfallRaw>(url)
    return data ? SnapshotCumulativeRainfallDTOFactory.fromRaw(data) : null
  }

  public async getAdministrativeAreas(cityName: string, ref: DateTime): Promise<AdministrativeAreaDTO[] | null> {
    const data = await this.safeGet<{ reference: AdministrativeAreaRaw[] }>(
      this.routes.getAdministrativeAreas(cityName, ref),
    )

    if (!data) return null

    return data.reference.map((r) => AdministrativeAreaDTOFactory.fromRaw(r))
  }

  public async getAdministrativeAreasForecast(
    cityName: string,
    ref: DateTime,
    forecast: DateTime,
  ): Promise<AdministrativeAreaDTO[] | null> {
    const data = await this.safeGet<{ reference: AdministrativeAreaRaw[] }>(
      this.routes.getAdministrativeAreasForecast(cityName, ref, forecast),
    )

    if (!data) return null

    return data.reference.map((r) => AdministrativeAreaDTOFactory.fromRaw(r))
  }

  public async getCatchments(cityName: string, ref: DateTime): Promise<CatchmentDTO[] | null> {
    const data = await this.safeGet<{ reference: CatchmentRaw[] }>(this.routes.getCatchments(cityName, ref))

    if (!data) return null

    return data.reference.map((c) => CatchmentDTOFactory.fromRaw(c))
  }

  public async getCatchmentsForecast(
    cityName: string,
    ref: DateTime,
    forecast: DateTime,
  ): Promise<CatchmentDTO[] | null> {
    const data = await this.safeGet<{ reference: CatchmentRaw[] }>(
      this.routes.getCatchmentsForecast(cityName, ref, forecast),
    )

    if (!data) return null

    return data.reference.map((c) => CatchmentDTOFactory.fromRaw(c))
  }

  public getRainfallMap(city: string, ref: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getRainfallMap(city, ref))
  }

  public getRainfallForecastMap(city: string, ref: DateTime, forecast: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getRainfallForecastMap(city, ref, forecast))
  }

  public getRainfallMaxMap(city: string, ref: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getRainfallMaxMap(city, ref))
  }

  public getCumulativeRainfallMap(city: string, cumulative: number, ref: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getCumulativeRainfallMap(city, cumulative, ref))
  }

  public getCumulativeRainfallForecastMap(
    city: string,
    cumulative: number,
    ref: DateTime,
    forecast: DateTime,
  ): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getCumulativeRainfallForecastMap(city, cumulative, ref, forecast))
  }

  public getReturnPeriodMap(city: string, ref: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getReturnPeriodMap(city, ref))
  }

  public getReturnPeriodForecastMap(city: string, ref: DateTime, forecast: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getReturnPeriodForecastMap(city, ref, forecast))
  }

  public getReturnPeriodMaxMap(city: string, ref: DateTime): Promise<GeoJSON | null> {
    return this.safeGet<GeoJSON>(this.routes.getReturnPeriodMaxMap(city, ref))
  }

  private handleError(url: string, error: AxiosError<ServerError>): void {
    if (error?.request?.status === 500) {
      console.error(`Server error on request to: ${url}`)
      console.error(error)
    }
  }
}
